<template>
  <div class="w-tree">
    <FwCause
      ref="cause"
      :selecting-causes="selectingCauses"
      :cause="cause"
      :style="tileStyle"
      :index="index"
      :fw-root="fwRoot"
      @taResize="diffusePlaceCanvas"
    />
    <div class="w-tree-row">
      <FwCausesTree
        v-for="(causeChild, cIndex) in cause.wCauses"
        :key="cIndex"
        ref="children"
        :selecting-causes="selectingCauses"
        :cause="causeChild"
        :index="cIndex"
        :fw-root="fwRoot"
        @edit="edit()"
      />
      <div
        ref="addBtnWrapper"
        class="generic-border generic-border--icon-button generic-border--gray-border w-tree__add"
        :style="addStyle"
        @click="addCause()"
      >
        <Icon>{{ addExtraBold }}</Icon>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Element of a 5 whys causes tree
 * @Param cause: the cause object with its causes array
 * @Param index: the index of the cause within it's parent's causes array
 */

import $ from 'jquery';
import toastr from 'toastr';
// Vuex
import { EDIT_CAUSE } from '@/store/modules/problem/action-types';
// Components
import FwCause from '@/components/fw-cause.vue';
import Icon from '@/components/icon.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Utils
import { SColors } from '@/utils/constants';
import { addExtraBold } from '@/assets/icons';

export default {

  name: 'FwCausesTree',

  components: {
    FwCause,
    Icon,
  },

  mixins: [mixinSocketHandler],

  props: {
    cause: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    fwRoot: {
      type: Object,
      default: undefined,
    },
    selectingCauses: Boolean,
  },

  data() {
    return {
      addExtraBold,
      linkCanvas: null,
    };
  },

  computed: {
    addStyle() {
      return {
        margin: this.cause.wCauses.length === 0
          ? `${this.fwRoot.measures.addTopMargin}px ${this.fwRoot.measures.addSidesMargin}px`
          : `${this.fwRoot.measures.addTopMargin}px 4px ${this.fwRoot.measures.addTopMargin}px 0`,
      };
    },
    tileStyle() {
      return `width:${this.fwRoot.measures.tileWidth}px;margin:${this.fwRoot.measures.tileMargin}px;`
              + 'background: white;';
    },
  },

  mounted() {
    this.initDrawLinks();
    // Drop sticker to add btn
    $(this.$refs.addBtnWrapper).droppable({
      accept: '.colored-sticker, .w-cause',
      tolerance: 'pointer',
      drop: (e, ui) => {
        const helper = ui.helper.length ? ui.helper[0] : ui.helper;
        const id = Number(helper.dataset.id);
        const { type } = helper.dataset;
        if (type === 'cause') {
          if (!this.cause.wCauses.some(cause => cause.id === id)) {
            this.$store.dispatch(`problem/${EDIT_CAUSE}`, { id, problemId: this.cause.problem, cause: this.cause.id })
              .then(() => {
                toastr.remove();
                toastr.success(this.$t('message.CAUSE_SAVED'));
                this.sendSocketUpdate();
              });
          }
        } else {
          const stickerIdx = this.fwRoot.stickers.findIndex(sticker => sticker.id === id);
          if (stickerIdx >= 0) {
            this.fwRoot.addCause(this.cause, this.fwRoot.stickers[stickerIdx].text);
            this.fwRoot.stickers.splice(stickerIdx, 1);
            this.fwRoot.deleteSticker(id);
          }
        }
      },
    });
  },

  beforeDestroy() {
    if (this.linkCanvas) {
      $(this.linkCanvas).remove();
    }
    const inst = $(this.$refs.addBtn).droppable('instance');
    if (inst) {
      $(this.$refs.addBtn).droppable('destroy');
    }
  },

  methods: {
    addCause(text) {
      this.fwRoot.addCause(this.cause, text);
    },
    /** Child has been edited */
    edit() {
      // Propagate to parent
      this.$emit('edit');
      // And to children
      this.diffuseEdit();
    },
    /** diffuse edit event to children to redraw canvases */
    diffuseEdit() {
      setTimeout(() => {
        this.initDrawLinks();
        const { children } = this.$refs;
        if (children) {
          for (let i = 0, len = children.length; i < len; i++) {
            if (children[i].diffuseEdit) {
              children[i].diffuseEdit();
            }
          }
        }
      }, 1);
    },
    /** Draw link to parent and link to add button */
    initDrawLinks() {
      if (this.linkCanvas) {
        $(this.linkCanvas).remove();
      }
      this.linkCanvas = this.drawLinks();
    },
    diffusePlaceCanvas() {
      setTimeout(() => {
        this.placeCanvas();
        const { children } = this.$refs;
        if (children) {
          for (let i = 0, len = children.length; i < len; i++) {
            if (children[i].diffusePlaceCanvas) {
              children[i].diffusePlaceCanvas();
            }
          }
        }
      }, 1);
    },
    placeCanvas() {
      if (!this.linkCanvas) {
        return;
      }
      const parent = this.$refs.cause.$el;
      const parentRect = parent.getBoundingClientRect();
      const parentHeight = parentRect.bottom - parentRect.top;
      this.linkCanvas.style.top = `${parent.offsetTop + parentHeight}px`;
    },
    drawLinks() {
      if (!this.fwRoot.measures.container || !this.$refs.cause) {
        return null;
      }
      // Create a link between the parent and the child
      const parent = this.$refs.cause.$el;
      let children = this.$refs.children || [];
      children = children.slice();
      for (let i = 0; i < children.length; i++) {
        children[i] = children[i].$el.querySelector('.w-cause');
      }
      children.push(this.$refs.addBtnWrapper);
      const parentRect = parent.getBoundingClientRect();
      const parentHeight = parentRect.bottom - parentRect.top;
      const lastChildRect = this.$refs.addBtnWrapper.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      canvas.width = lastChildRect.right - parentRect.left;
      canvas.height = this.$refs.addBtnWrapper.offsetTop - (parent.offsetTop + parentHeight) + 15;
      canvas.style.position = 'absolute';
      canvas.style.top = `${parentHeight + this.fwRoot.measures.tileMargin}px`;
      canvas.style.left = `${this.fwRoot.measures.tileMargin}px`;
      canvas.style.width = `${canvas.width}px`;
      canvas.style.height = `${canvas.height}px`;

      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(this.fwRoot.measures.halfTileWidth, 0);

      ctx.lineTo(this.fwRoot.measures.halfTileWidth, this.fwRoot.measures.tileMargin);
      for (let i = 0, len = children.length; i < len; i++) {
        const child = children[i];
        const childRect = child.getBoundingClientRect();
        const shift = childRect.left - parentRect.left + child.offsetWidth / 2;
        if (shift - this.fwRoot.measures.halfTileWidth > 1) {
          ctx.lineTo(shift, this.fwRoot.measures.tileMargin);
          ctx.lineTo(shift, childRect.top - parentRect.bottom);
        } else {
          ctx.lineTo(this.fwRoot.measures.halfTileWidth, childRect.top - parentRect.bottom);
        }
        ctx.moveTo(shift, this.fwRoot.measures.tileMargin);
      }
      ctx.strokeStyle = SColors.mainTextColor;
      ctx.stroke();
      this.$el.prepend(canvas);
      return canvas;
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({
        msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
        id: this.problemId,
        socketCompany: 1,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.w-tree {
  position: relative;
  margin-left: 0.7rem;

  .w-tree {
    margin-left: 0;
  }

  &__add {
    &.ui-droppable-active.generic-border--gray-border {
      border: dashed 3px $color-shizen-blue-btn-shadow-color;
    }
    &.ui-droppable-hover.generic-border--gray-border {
      border: solid 3px $color-shizen-blue-btn-shadow-color;
      background: $color-shizen-blue-btn-shadow-color;
    }
  }
}
</style>
